export const PRICE_TIERS = ["standard", "member", "resident"];

const priceKeyAliases = {
  standard: ["standard", "nonmember", "nonmembers", "non_member", "non_members", "non-member", "non-members"],
  member: ["member", "members"],
  resident: ["resident", "residents", "resident_doctor", "resident_doctors", "resident-doctor", "resident-doctors", "residentDoctor", "residentDoctors"],
};

const normalizeKey = (value) => String(value || "").trim().toLowerCase().replace(/[\s-]+/g, "_");

const getTierValue = (values, tier) => {
  if (!values || typeof values !== "object") {
    return undefined;
  }

  const aliases = priceKeyAliases[tier] || [tier];
  const matchingKey = Object.keys(values).find((key) =>
    aliases.some((alias) => normalizeKey(alias) === normalizeKey(key))
  );

  return matchingKey ? values[matchingKey] : undefined;
};

const normalizeRegistrationFee = (registrationFee) => {
  if (!registrationFee) {
    return null;
  }

  if (typeof registrationFee === "string") {
    try {
      return JSON.parse(registrationFee);
    } catch {
      return null;
    }
  }

  return registrationFee;
};

export const toValidAmount = (value) => {
  if (value === undefined || value === null || value === "") {
    return null;
  }
  const amount = Number(value);
  return Number.isFinite(amount) && amount >= 0 ? Math.round(amount) : null;
};

export const formatAmount = (amount, currency = "MNT") => {
  const numericAmount = Number(amount);
  if (!Number.isFinite(numericAmount)) {
    return "";
  }
  const formattedAmount = new Intl.NumberFormat("mn-MN", {
    style: "decimal",
    maximumFractionDigits: 0,
  }).format(numericAmount);
  return currency === "MNT" ? `${formattedAmount} ₮` : `${formattedAmount} ${currency}`;
};

export const formatPricingDate = (value, language) => {
  const parsedMs = parsePricingDate(value);
  if (parsedMs === null) {
    return "";
  }

  return new Date(parsedMs).toLocaleString(language ? "en-US" : "mn-MN", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const parsePricingDate = (value) => {
  if (!value) {
    return null;
  }
  const rawValue = String(value).trim();
  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(rawValue);
  const isNaiveDateTime = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(rawValue);

  if (isNaiveDateTime && !hasTimezone) {
    const normalized = rawValue.replace(" ", "T");
    const withSeconds = normalized.length === 16 ? `${normalized}:00` : normalized;
    const parsedUlaanbaatarTime = Date.parse(`${withSeconds}+08:00`);
    return Number.isNaN(parsedUlaanbaatarTime) ? null : parsedUlaanbaatarTime;
  }

  const parsedTime = Date.parse(rawValue);
  return Number.isNaN(parsedTime) ? null : parsedTime;
};

const appliesToTier = (appliesTo, tier) => {
  if (!Array.isArray(appliesTo) || appliesTo.length === 0) {
    return true;
  }

  const aliases = priceKeyAliases[tier] || [tier];
  return appliesTo.some((value) => {
    const normalizedValue = normalizeKey(value);
    return normalizedValue === "all" || normalizedValue === "*" || aliases.some((alias) => normalizeKey(alias) === normalizedValue);
  });
};

export const getActiveDiscount = (discounts, tier) => {
  const now = Date.now();
  return (discounts || []).find((discount) => {
    if (!discount?.active) {
      return false;
    }
    if (!appliesToTier(discount.applies_to, tier)) {
      return false;
    }
    const startMs = parsePricingDate(discount.starts_at);
    const endMs = parsePricingDate(discount.ends_at);
    if (discount.starts_at && startMs === null) {
      return false;
    }
    if (discount.ends_at && endMs === null) {
      return false;
    }
    if (startMs !== null && now < startMs) {
      return false;
    }
    if (endMs !== null && now > endMs) {
      return false;
    }
    return true;
  });
};

const getDiscountAmount = ({ discount, originalAmount, tier }) => {
  if (!discount) {
    return 0;
  }

  const discountedPrice = getDiscountedPrice(discount, tier);
  if (discountedPrice !== null) {
    return Math.max(0, Math.min(originalAmount, originalAmount - discountedPrice));
  }

  const discountValue = Number(getTierValue(discount.values, tier) ?? discount.value ?? 0);
  const rawDiscountAmount = discount.type === "percent"
    ? Math.round(originalAmount * discountValue / 100)
    : Math.round(discountValue);

  return Number.isFinite(rawDiscountAmount)
    ? Math.max(0, Math.min(originalAmount, rawDiscountAmount))
    : 0;
};

const getDiscountedPrice = (discount, tier) => {
  if (!discount) {
    return null;
  }

  return toValidAmount(
    getTierValue(discount.prices, tier) ??
    getTierValue(discount.discounted_prices, tier) ??
    getTierValue(discount.discountedPrices, tier)
  );
};

export const getPricingForTier = ({ meetingInfo, tier, allowDiscountOnly = false }) => {
  const registrationFee = normalizeRegistrationFee(meetingInfo?.registration_fee || meetingInfo?.registrationFee);
  if (!registrationFee) {
    return null;
  }

  const prices = registrationFee?.prices || {};
  const discount = getActiveDiscount(registrationFee?.discounts, tier);
  const discountedPrice = getDiscountedPrice(discount, tier);
  const originalAmount = toValidAmount(getTierValue(prices, tier));
  if (originalAmount === null) {
    if (!allowDiscountOnly || discountedPrice === null) {
      return null;
    }

    return {
      tier: tier,
      currency: registrationFee?.currency || "MNT",
      amount: discountedPrice,
      original_amount: null,
      discount_amount: 0,
      discount_code: discount?.code || "",
      discount_label: discount?.label || "",
      discount_ends_at: discount?.ends_at || "",
      discount_only: true,
    };
  }

  const discountAmount = getDiscountAmount({ discount, originalAmount, tier });

  return {
    tier: tier,
    currency: registrationFee?.currency || "MNT",
    amount: Math.max(0, originalAmount - discountAmount),
    original_amount: originalAmount,
    discount_amount: discountAmount,
    discount_code: discount?.code || "",
    discount_label: discount?.label || "",
    discount_ends_at: discount?.ends_at || "",
  };
};

export const getPricingPreview = ({ meetingInfo, resident, membershipActive, allowDiscountOnly = false }) => {
  const tier = resident ? "resident" : membershipActive ? "member" : "standard";
  return getPricingForTier({ meetingInfo, tier, allowDiscountOnly });
};

export const isActiveMembership = (paidUntil) => {
  if (!paidUntil) {
    return false;
  }

  const paidUntilTime = new Date(paidUntil).getTime();
  return Number.isFinite(paidUntilTime) && paidUntilTime > Date.now();
};

export const hasMemberPricingAccess = (userData) => (
  !!userData?.id && isActiveMembership(userData.paid_until)
);

export const getPricingRows = (meetingInfo, options = {}) =>
  PRICE_TIERS
    .map((tier) => getPricingForTier({ meetingInfo, tier, ...options }))
    .filter(Boolean);

export const getTierLabel = (tier, language) => {
  const labels = language
    ? { standard: "Non-member", member: "Member", resident: "Resident doctor" }
    : { standard: "Гишүүн бус", member: "Гишүүн", resident: "Резидент эмч" };
  return labels[tier] || labels.standard;
};
